import AuditProvider from 'components/modules/service/Audit/AuditProvider';
import { memo } from 'react';
import PropTypes from 'prop-types';
import HistoryIcon from '@material-ui/icons/History';
import { ROWS_PER_PAGE } from 'consts';
import ExpansionList from '../../../../common/ExpansionList';
import { columnMeta } from './History/config/index';
import ExpandablePanel from '../../../../common/ExpandablePanel';
import {
  useIsFspConfigUpdating,
  useIsProfileLoading,
} from '../../../implementation/Fsp/Detail/common';
import GuestDetailsRender from './History/config/DetailsRender/GuestDetailsRender';
import GuestHistoryActionsRenderer from './History/config/GuestHistoryActionsRenderer';
import { getStatusLabel } from './History/config/DetailsRender/consts';
import useGuestTransactionList from './useGuestTransactionList';

const SORTABLE_COLUMNS = new Set([
  'transactionType',
  'sendOn',
  'fundingAccount',
  'destination',
  'amount',
  'status',
]);
const guestColumnMeta = columnMeta.map((column) =>
  SORTABLE_COLUMNS.has(column.sortId)
    ? column
    : { ...column, sortId: undefined }
);

const getSendOnTimestamp = (item) => {
  const timestamp = item?.sendOn ? new Date(item.sendOn).getTime() : NaN;
  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareValues = (left, right, sortId) => {
  if (sortId === 'status') {
    return String(getStatusLabel(left?.status) ?? '').localeCompare(
      String(getStatusLabel(right?.status) ?? '')
    );
  }
  if (sortId === 'transactionType') {
    return String(left?.transactionType ?? '').localeCompare(
      String(right?.transactionType ?? '')
    );
  }
  if (sortId === 'fundingAccount') {
    const fundingLabel = (item) =>
      [item?.fundingAccount, item?.fundingMask, item?.fundingAccountSuffix]
        .filter((value) => value !== undefined && value !== null)
        .join(' ');
    return fundingLabel(left).localeCompare(fundingLabel(right));
  }
  if (sortId === 'destination') {
    const destinationLabel = (item) =>
      [
        item?.destination,
        item?.coreAccountNumberMask,
        item?.destinationMask,
        item?.destinationAccountSuffix,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(' ');
    return destinationLabel(left).localeCompare(destinationLabel(right));
  }
  if (sortId === 'amount') {
    return (Number(left?.amount) || 0) - (Number(right?.amount) || 0);
  }
  return getSendOnTimestamp(left) - getSendOnTimestamp(right);
};

const HISTORY_QUERY_KEYS = {
  page: 'historyPage',
  rowsPerPage: 'historySize',
  orderBy: 'historyOrderBy',
  order: 'historyOrder',
};
const HISTORY_DATE_SORT_IDS = new Set(['sendOn']);

function guestDetailsRender(transaction) {
  return <GuestDetailsRender transaction={transaction} />;
}

const GuestTransactionHistoryPanel = ({
  items,
  totalRecords,
  onOpen,
  loading: transactionsLoading,
  error: transactionsError,
  status,
  authReady,
}) => {
  const profileLoading = useIsProfileLoading();
  const loading = !authReady || profileLoading || transactionsLoading;
  const updating = useIsFspConfigUpdating();
  const {
    handleListChange,
    incomplete,
    listItems,
    listTotal,
    page,
    pageItems,
    rowsPerPage,
    sort,
  } = useGuestTransactionList({
    items,
    totalRecords,
    queryKeys: HISTORY_QUERY_KEYS,
    compareValues,
    dateSortIds: HISTORY_DATE_SORT_IDS,
    getDateTimestamp: (item) => getSendOnTimestamp(item),
  });
  const showList = authReady && (listItems.length > 0 || status === 'success');

  const handleRetry = () => {
    if (onOpen) onOpen(undefined, true);
  };

  const handleExpand = (expanded) => {
    if (expanded && onOpen) onOpen();
  };

  return (
    <AuditProvider>
      <ExpandablePanel
        fullHeight={false}
        icon={HistoryIcon}
        id="guest-transaction-history"
        showProgress={loading || updating}
        title="Transaction History"
        onChange={handleExpand}
      >
        {transactionsError && (
          <div role="alert">
            {transactionsError}
            <button type="button" onClick={handleRetry} disabled={!authReady}>
              Retry
            </button>
          </div>
        )}
        {!authReady && (
          <div role="status">
            Waiting for authentication before loading transaction history.
          </div>
        )}
        {incomplete && (
          <div role="status">
            Some history records are unavailable. Please retry the panel.
            <button type="button" onClick={handleRetry}>
              Retry
            </button>
          </div>
        )}
        {showList && (
          <ExpansionList
            key={`history-${page}-${listItems.length}`}
            ActionsRenderer={GuestHistoryActionsRenderer}
            columnMeta={guestColumnMeta}
            detailsRender={guestDetailsRender}
            emptyTitle="No transaction history found."
            data={pageItems}
            name="guest-history"
            onChange={handleListChange}
            orderBy={sort.orderBy}
            order={sort.order}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE}
            total={listTotal}
          />
        )}
      </ExpandablePanel>
    </AuditProvider>
  );
};

GuestTransactionHistoryPanel.propTypes = {
  items: PropTypes.array,
  totalRecords: PropTypes.number,
  onOpen: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  status: PropTypes.string,
  authReady: PropTypes.bool,
};

GuestTransactionHistoryPanel.defaultProps = {
  items: [],
  totalRecords: undefined,
  onOpen: undefined,
  loading: false,
  error: null,
  status: 'idle',
  authReady: false,
};

export default memo(GuestTransactionHistoryPanel);
